import { Command } from "commander";
import {
  readFileSync,
  existsSync,
  writeFileSync,
  mkdirSync,
  readdirSync,
} from "node:fs";
import { join, basename, dirname, resolve } from "node:path";
import { homedir } from "node:os";
import chalk from "chalk";

// ── Types ──────────────────────────────────────────────────

export interface ImportSessionRecord {
  sessionId: string;
  featureId?: string;
  confidence: number;
  source: string;
  status: string;
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  model: string;
  sessionStart: number;
  sessionEnd?: number;
  activeMinutes?: number;
  tokenSource: string;
  trackedAt: string;
}

// ── Claude Code parser ─────────────────────────────────────

/**
 * Walk a directory tree and collect all .jsonl file paths.
 */
function findJsonlFiles(dir: string): string[] {
  if (!existsSync(dir)) return [];
  const files: string[] = [];

  function walk(d: string) {
    const entries = readdirSync(d, { withFileTypes: true });
    for (const entry of entries) {
      const fullPath = join(d, entry.name);
      if (entry.isDirectory()) {
        walk(fullPath);
      } else if (entry.name.endsWith(".jsonl")) {
        files.push(fullPath);
      }
    }
  }

  walk(dir);
  return files;
}

/**
 * Parse a single JSONL file into an ImportSessionRecord.
 * Extracts token usage, model, and timestamps from each JSON line.
 */
function parseJsonlFile(filePath: string): ImportSessionRecord | null {
  const content = readFileSync(filePath, "utf-8");
  const lines = content.trim().split("\n").filter(Boolean);

  if (lines.length === 0) return null;

  let inputTokens = 0;
  let outputTokens = 0;
  let cacheReadTokens = 0;
  let model = "unknown";
  let hasApiUsage = false;
  let sessionStart = 0;
  let sessionEnd = 0;

  for (const line of lines) {
    let entry: Record<string, unknown>;
    try {
      entry = JSON.parse(line);
    } catch {
      continue;
    }

    // Extract timestamp
    const ts = entry.timestamp as string | undefined;
    if (ts) {
      const time = new Date(ts).getTime();
      if (!sessionStart || time < sessionStart) sessionStart = time;
      if (time > sessionEnd) sessionEnd = time;
    }

    // Extract usage
    const usage = entry.usage as Record<string, number> | undefined;
    if (usage) {
      inputTokens += usage.input_tokens ?? 0;
      outputTokens += usage.output_tokens ?? 0;
      cacheReadTokens += usage.cache_read_input_tokens ?? 0;
      if ((usage.input_tokens ?? 0) > 0 || (usage.output_tokens ?? 0) > 0) {
        hasApiUsage = true;
      }
    }

    // Extract model
    if (entry.model && typeof entry.model === "string") {
      model = entry.model;
    }
  }

  // Skip files with no meaningful data
  if (!hasApiUsage && model === "unknown") return null;

  const fileBase = basename(filePath, ".jsonl");
  const activeMinutes =
    sessionStart && sessionEnd
      ? (sessionEnd - sessionStart) / 60000
      : undefined;

  return {
    sessionId: `import-claude-${fileBase}`,
    model,
    inputTokens,
    outputTokens,
    cacheReadTokens,
    source: "import-claude",
    status: "imported",
    confidence: 0.5,
    tokenSource: hasApiUsage ? "api" : "none",
    trackedAt: new Date().toISOString(),
    sessionStart,
    sessionEnd: sessionEnd || undefined,
    activeMinutes,
  };
}

/**
 * Parse Claude Code session data from a directory of JSONL files.
 * Default path: ~/.claude/projects/
 */
export function parseClaude(basePath?: string): ImportSessionRecord[] {
  const dir = basePath ?? join(homedir(), ".claude", "projects");
  const jsonlFiles = findJsonlFiles(dir);
  const results: ImportSessionRecord[] = [];

  for (const file of jsonlFiles) {
    const record = parseJsonlFile(file);
    if (record) results.push(record);
  }

  return results;
}

// ── Cursor stub ────────────────────────────────────────────

/**
 * Cursor import — planned for a future release.
 */
export function parseCursor(): ImportSessionRecord[] {
  console.log(
    chalk.yellow(
      "Cursor import is planned for a future release. Contribute at github.com/nicobailon/fathom",
    ),
  );
  return [];
}

// ── Copilot stub ───────────────────────────────────────────

/**
 * Copilot import — planned for a future release.
 */
export function parseCopilot(): ImportSessionRecord[] {
  console.log(
    chalk.yellow(
      "Copilot import is planned for a future release. Contribute at github.com/nicobailon/fathom",
    ),
  );
  return [];
}

// ── Merge logic ────────────────────────────────────────────

export interface MergeResult {
  imported: number;
  skipped: number;
  sessions: ImportSessionRecord[];
}

/**
 * Merge imported sessions into summary.json, deduplicating by sessionId.
 * If dryRun is true, computes the merge but does not write the file.
 */
export function mergeIntoSummary(
  newSessions: ImportSessionRecord[],
  summaryPath: string,
  dryRun: boolean,
): MergeResult {
  // Read existing summary or create empty
  let existing: { project?: string; sessions: ImportSessionRecord[]; lastUpdated?: string } = {
    sessions: [],
  };

  if (existsSync(summaryPath)) {
    try {
      existing = JSON.parse(readFileSync(summaryPath, "utf-8"));
    } catch {
      // If corrupt, start fresh
      existing = { sessions: [] };
    }
  }

  const existingIds = new Set(existing.sessions.map((s) => s.sessionId));

  const toImport: ImportSessionRecord[] = [];
  let skipped = 0;

  for (const session of newSessions) {
    if (existingIds.has(session.sessionId)) {
      skipped++;
    } else {
      toImport.push(session);
    }
  }

  if (!dryRun && toImport.length > 0) {
    existing.sessions.push(...toImport);

    // Ensure parent directory exists
    const parentDir = dirname(summaryPath);
    mkdirSync(parentDir, { recursive: true });

    writeFileSync(
      summaryPath,
      JSON.stringify(
        {
          project: existing.project ?? "imported",
          sessions: existing.sessions,
          lastUpdated: new Date().toISOString(),
        },
        null,
        2,
      ),
    );
  }

  return {
    imported: toImport.length,
    skipped,
    sessions: toImport,
  };
}

// ── CLI Command ────────────────────────────────────────────

export const importCommand = new Command("import")
  .description("Import AI usage data from external tools")
  .requiredOption(
    "--source <source>",
    "Source tool to import from (claude, cursor, copilot)",
  )
  .option("--path <path>", "Override default source directory")
  .option("--json", "Output as JSON")
  .option("--dry-run", "Preview without writing")
  .action(
    (options: {
      source: string;
      path?: string;
      json?: boolean;
      dryRun?: boolean;
    }) => {
      const validSources = ["claude", "cursor", "copilot"];
      if (!validSources.includes(options.source)) {
        console.log(
          chalk.red(
            `Unknown source "${options.source}". Valid sources: ${validSources.join(", ")}`,
          ),
        );
        return;
      }

      // Parse sessions based on source
      let sessions: ImportSessionRecord[];

      switch (options.source) {
        case "claude":
          console.log(
            chalk.dim(`Scanning ${options.path ?? "~/.claude/projects/"}...`),
          );
          sessions = parseClaude(options.path);
          break;
        case "cursor":
          sessions = parseCursor();
          return;
        case "copilot":
          sessions = parseCopilot();
          return;
        default:
          return;
      }

      if (sessions.length === 0) {
        console.log(chalk.dim("No sessions found to import."));
        return;
      }

      // Merge into summary.json
      const summaryPath = resolve(
        process.cwd(),
        ".fathom",
        "tracking",
        "summary.json",
      );

      const result = mergeIntoSummary(sessions, summaryPath, options.dryRun ?? false);

      // Output
      if (options.json) {
        process.stdout.write(
          JSON.stringify(
            {
              imported: result.imported,
              skipped: result.skipped,
              sessions: result.sessions,
            },
            null,
            2,
          ) + "\n",
        );
        return;
      }

      if (options.dryRun) {
        console.log(
          chalk.dim(
            `Would import ${result.imported} sessions (dry run, no changes written)`,
          ),
        );
        if (result.skipped > 0) {
          console.log(chalk.dim(`  ${result.skipped} skipped as duplicates`));
        }
      } else {
        console.log(
          chalk.green(
            `Imported ${result.imported} new sessions` +
              (result.skipped > 0
                ? ` (${result.skipped} skipped as duplicates)`
                : ""),
          ),
        );
        if (result.imported > 0) {
          console.log(chalk.dim(`  Saved to ${summaryPath}`));
        }
      }
    },
  );
